<script setup>
import { get_trait, get_package } from '@/services/option';
import { get_interventions, delete_intervention } from '@/services/interventions';
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { get_terms } from '@/services/terms';
import { swalError, swalSuccess, getLowerAge } from '@/helper';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'
import '@splidejs/splide/dist/css/splide.min.css'


const route = useRoute()
const isLoading = ref(true)
const package_config = ref({})
const trait = ref({})
const interventions = ref([])
const filterOption = ref()
onMounted(async ()=>{
  let response = await get_package(route.params.package_id)
  if (response.error) {
    if (!response.isRedirecting) swalError(response.message || 'Failed to load package')
    return
  }
  package_config.value = response.result
  
  let trait_response = await get_trait(route.params.trait_id)
  if (trait_response.error) {
    if (!trait_response.isRedirecting) swalError(trait_response.message || 'Failed to load trait')
    return
  }
  trait.value = trait_response.result
  
  let intervention_response = await get_interventions(route.params.trait_id)
  if (intervention_response.error) {
    if (!intervention_response.isRedirecting) swalError(intervention_response.message || 'Failed to load interventions')
    return
  }
  interventions.value = intervention_response.result?.data?.data || []

  // get filter option
  let age_group_response = await get_terms('age_group_int')
  if (age_group_response.error) {
    if (!age_group_response.isRedirecting) swalError(age_group_response.message || 'Failed to load age groups')
    return
  }
  filterOption.value = sortAgeGroup(age_group_response.result?.data?.data || []).map(item => {
    return {
      ...item,
      isActive: false
    }
  })
  filterOption.value.splice(0,0,{id: null, attributes: {name: 'All'}})
  
  isLoading.value = false
})

function sortAgeGroup(response){
  response.sort((a, b) => getLowerAge(a.attributes.name) - getLowerAge(b.attributes.name));
  return response
}

const isAssessmentLoading = ref(false)
const currentFilter = ref({})
async function filterAssessment(termObj = null){
  filterOption.value.map(item => item.isActive = false)
  termObj.isActive = true
  currentFilter.value = termObj
  isAssessmentLoading.value = true
  let response = ''
  if (termObj.id){
    response = await get_interventions(route.params.trait_id, termObj.relationships.term_taxonomy.data.id)
  }else{
    response = await get_interventions(route.params.trait_id)
  }
  // console.log("🚀 ~ file: interventionList.vue:34 ~ filterAssessment ~ response:", response.result.data.data)
  if (response.error){
    if (!response.isRedirecting) {
      swalError(response.message || 'Failed to load interventions')
    }
    isAssessmentLoading.value = false
    return
  }
  interventions.value = response.result?.data?.data || []
  isAssessmentLoading.value = false
}

async function remove_intervention(intervention_id){
  let response = await delete_intervention(intervention_id)
  if (response.error){
    swalError(response.result)
    return
  }
  swalSuccess('Delete successfully')
  let interventionInd = interventions.value.findIndex(item => item.id == intervention_id)
  interventions.value.splice(interventionInd, 1)
}

const slideOption = {
  type: 'slide',
  autoWidth: true,
  arrows: true,
  pagination: false,
  perMove: 4
}
</script>

<template>
  <div class="container-fluid">
    <Breadcrumbs 
      main="Interventions" 
      :title="(trait.data?.attributes.name || '') + ' List'"
      :mainRoute="{name: 'package-intervention-list'}"
    />

    <LoadingCard v-if="isLoading" />
    <template v-else>
      <!-- Page title -->
      <div class="d-flex align-items-center">
        <div>
          <h4 class="mb-0">{{ trait.name }}</h4>
        </div>
        <div class="ms-3">
          <router-link :to="{
              name: 'new-intervention', 
              params: {package_id: route.params.package_id, trait_id: route.params.trait_id},
              query: {age_group: currentFilter.id}
            }"
            class="btn btn-primary"
          >
            Add New Intervention

          </router-link>
        </div>
      </div>
      
      <!-- Age Group Filter -->
      <div class="row my-3">
        <Splide
          :has-track="false"
          :options="slideOption"
          class="age-group-splide"
        >
          <div class="splide__arrows">
            <button class="splide__arrow splide__arrow--prev">
              <vue-feather type="chevron-right"/>
            </button>
            <button class="splide__arrow splide__arrow--next">
              <vue-feather type="chevron-right"/>
            </button>
          </div>
          <SplideTrack>
            <SplideSlide v-for="filterObj in filterOption" :key="filterObj.id">
              <button  
              class="shadow-none me-2 btn btn-light-primary shadow px-4 py-2 rounded-pill age-btn"
              :class="{'active': filterObj.isActive}"
              @click="filterAssessment(filterObj)"
            >
              {{ filterObj.attributes.name }}
            </button>
            </SplideSlide>
          </SplideTrack>
        </Splide>
      </div>

      <EmptyCard v-if="interventions.length === 0"
        :link="{
          routerObj:{
            name: 'dashboard'
          },
          type: 'link',
          desc: 'Back to home',
          class: 'w-50'
        }"
      />
      <template v-else>
        <LoadingCard v-if="isAssessmentLoading" />
        <div v-else class="card">
          <div class="card-body">
            <div class="card shadow-none border">
              <div class="card-body p-0 table-responsive">
                <table class="table user-table">
                  <thead>
                    <tr>
                      <th class="col-9">Description</th>
                      <th class="col-3 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="intervention in interventions" class="border-bottom">
                      <td class="border-0">
                        <p class="clamp-2">
                          {{ intervention.attributes.name }}
                        </p>
                      </td>
                      <td class="d-flex align-items-center border-0 justify-content-center">
                        <router-link :to="{name: 'edit-intervention', params: {id: intervention.id}}"
                          class="btn btn-light-success btn-icon"
                        >
                          <vue-feather type="edit-2" size="1rem" />
                        </router-link>
                        <button class="ms-2 btn btn-light-danger btn-icon"
                          @click="remove_intervention(intervention.id)"
                        >
                          <vue-feather type="trash" size="1rem" />
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<style scoped>
.age-group-splide{
  width: 70vw;
  margin: auto
}

.splide__arrow--prev{
  left: -2.5rem;
}
.splide__arrow--next{
  right: -2.5rem;
}
</style>